import { Field, FormSpy } from "react-final-form";
import { Divider, Modal, Tooltip, message } from "antd";
import { RadioField, TitlePage, PhoneField, CustomButton, Icon, FeatureToggle } from "~/components";
import { composeValidators, disabledValidate, required, testRegExp } from "~/utils/validate";
import { FormField, PayformNameField, PaymentMethodsSelectField, SocialLinkWithModalField } from "~/pages/questionnaires/shared";
import { TextareaModalField } from "~/new-ui-customer-questionnaire/components/TextareaModalField/TextareaModalField";
import { NamesOfFieldEnum, OrgTypeEnum } from "~/typings/types";
import { configTitleFieldsOfSelfEmployed } from "../configTitleFields";
import { V2Wizard, PagePreviewModal, FieldGenerateLogo } from "../../../../shared";
import { FullWidthRowFormContainer, RowFieldWithButtonsContainer } from "~/pages/questionnaires/shared/form-field";
import styles from "./CommonSteps.module.scss";
import { useScrollAndFocusField } from "~/hooks";
import { useEffect, useRef, useState } from "react";
import { useAnalyticsContext } from "~/services/analytics";
import cn from 'classnames';

const getProductDescriptionConfig = (productType: string) => {
  switch (productType) {
    case "goods":
      return {
        title: "Опишите товары, которые сейчас продаёте",
        placeholder: "Расскажите о ваших товарах просто и понятно. Опишите, какую пользу получает клиент и какую задачу решает ваш продукт."
      };
    case "services":
      return {
        title: "Опишите услуги, которые сейчас предоставляете",
        placeholder: "Расскажите о ваших услугах просто и понятно. Опишите, какую пользу получает клиент и какую задачу решает ваш продукт."
      };
    case "education":
      return {
        title: "Опишите курсы, которые сейчас проводите",
        placeholder: "Расскажите о ваших курсах просто и понятно. Опишите, какую пользу получает клиент и какую задачу решает ваш курс."
      };
    default:
      return {
        title: "Расскажите о вашем продукте или услуге",
        placeholder: "Расскажите о ваших услугах просто и понятно. Опишите, какую пользу получает клиент и какую задачу решает ваш продукт."
      };
  }
};

const RubleSvg = ({ size = 18, color = '#8c8e94' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M18.6081 8H36.7829C40.4023 8 43.5223 8.61794 46.1011 9.90454C48.661 11.1607 50.6337 12.9301 51.9763 15.2187C53.3342 17.4759 54 20.0578 54 22.9327C54 26.0241 53.341 28.7356 51.9768 31.0249C50.6298 33.3002 48.6486 35.0314 46.0787 36.2244L46.0721 36.2274C43.5017 37.398 40.3928 37.9602 36.7829 37.9602H26.7374V40.2885H37.3998V47.2802H26.7374V56H18.6081V47.2802H12V40.2885H18.6081V37.9602H12V31H18.6081V8ZM20.6081 10V33H14V35.9602H20.6081V42.2885H14V45.2802H20.6081V54H24.7374V45.2802H35.3998V42.2885H24.7374V35.9602H36.7829C40.1946 35.9602 43.0011 35.4276 45.24 34.4087C47.4779 33.3692 49.1348 31.8996 50.256 30.0057L50.2576 30.003C51.4038 28.0804 52 25.7378 52 22.9327C52 20.3757 51.4111 18.1571 50.2599 16.2453L50.2537 16.2351C49.1282 14.3146 47.4638 12.8005 45.2173 11.6987L45.211 11.6955C42.9806 10.5821 40.1852 10 36.7829 10H20.6081ZM24.7374 12.9918H36.7829C39.3178 12.9918 41.4626 13.452 43.1382 14.4604C44.7694 15.4296 45.9909 16.6927 46.7473 18.2595C47.4937 19.762 47.8707 21.3444 47.8707 22.9959C47.8707 24.9585 47.483 26.7157 46.6693 28.2341C45.8596 29.7852 44.5999 30.9695 42.9407 31.7905C41.2696 32.6174 39.1993 33 36.7829 33H24.7374V12.9918ZM26.7374 14.9918V31H36.7829C39.0061 31 40.7453 30.6454 42.0537 29.998C43.3721 29.3456 44.3034 28.4466 44.8985 27.3044L44.9042 27.2935C45.5357 26.1171 45.8707 24.6957 45.8707 22.9959C45.8707 21.6586 45.5676 20.3785 44.9538 19.1446L44.9482 19.1333C44.3854 17.9654 43.4594 16.9772 42.1143 16.1785L42.1089 16.1753C40.8479 15.4157 39.099 14.9918 36.7829 14.9918H26.7374Z" fill={color} />
  </svg>
);

const RubleIconBordered = () => {
  const [hovered, setHovered] = useState(false);
  const color = hovered ? '#000' : '#1b1f3b';
  const borderColor = hovered ? '#1b1f3b' : 'rgba(27, 31, 59, 0.4)';

  return (
    <span
      style={{ display: 'inline-flex', flexShrink: 0 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Tooltip title="Платная услуга" placement="top" arrow={false} overlayInnerStyle={{ backgroundColor: '#8C8E94', padding: 20, fontSize: 14, fontFamily: 'Roboto', lineHeight: '24px' }}>
        <span
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: `2px solid ${borderColor}`, borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', flexShrink: 0, transition: 'border-color 0.3s ease' }}
        >
          <RubleSvg size={13} color={color} />
        </span>
      </Tooltip>
    </span>
  );
};

type WarningField = 'integrations' | 'recurrent' | null;

export const PayformTechDetails = ({
  paymentMethod,
  productType,
  typeRequestCheckPayformName,
  setTypeRequestCheckPayformName,
  callbackCheckPayformName,
  isLoadingMount,
  setLogoFileId,
  logoError,
  setLogoError,
  errorMsgCheckPayformName,
  isFirstRenderPayformTechDetailsStep,
  setIsFirstRenderPayformTechDetailsStep,
  clientId,
  logoData,
  setLogoData,
  setIsLogoProcessing,
  orgType,
}: any) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [currentLogoSrc, setCurrentLogoSrc] = useState('');
  const [warningModal, setWarningModal] = useState<{ field: WarningField; onConfirm: () => void } | null>(null);
  const [hasAnyYes, setHasAnyYes] = useState(false);
  const [productTypeChangeConfirm, setProductTypeChangeConfirm] = useState<{ newValue: string; form: any } | null>(null);

  const isInitDone = useRef(false);

  useScrollAndFocusField(NamesOfFieldEnum.ProductName);

  const analytics = useAnalyticsContext();
  analytics?.trackStep('anketa-customer-se-init_step4');
  analytics?.trackStepWebvisor('webvisor_anketa_customer_step3');

  useEffect(() => {
    setIsFirstRenderPayformTechDetailsStep(true);
  }, []);

  return (
    <V2Wizard.Page>
      <TitlePage
        name="Настройте платёжную страницу"
        number="4"
      />
      <Divider style={{ margin: 0 }} />
      <div className={styles.container}>
        <FormSpy subscription={{ values: true }}>
          {({ values, form }) => {
            if (isFirstRenderPayformTechDetailsStep && !isInitDone.current) {
              isInitDone.current = true;

              form.change(
                NamesOfFieldEnum.PayformEmail,
                values?.[NamesOfFieldEnum.CrmEmail]
              );
              form.change(
                NamesOfFieldEnum.PayformPhone,
                values?.[NamesOfFieldEnum.CrmPhone]
              );

              if (paymentMethod) {
                const current = values?.[NamesOfFieldEnum.PayformPaymentMethods];
                if (!current || current.length === 0) {
                  // Задача: https://jira.tasktracker.help/browse/LKP-2808, => "cards_rf", "installments_rf" сделать анчекнутыми по умолчанию
                  const allNames = Object.keys(paymentMethod).filter(key => ["cards_rf", "installments_rf"].includes(key));
                  form.change(NamesOfFieldEnum.PayformPaymentMethods, allNames);
                }
              }
            }
            return null;
          }}
        </FormSpy>

        {productType && Object.keys(productType).length && (
          <FullWidthRowFormContainer>
            <FormSpy subscription={{ values: true }}>
              {({ values: spyValues, form: spyForm }) => (
                <FormField
                  titleField={configTitleFieldsOfSelfEmployed[NamesOfFieldEnum.ProductType]}
                  name={NamesOfFieldEnum.ProductType}
                  tooltip="Выберите, что будете продавать. Если ваш продукт подходит к нескольким вариантам, то выберите тот, где продаж больше всего"
                  isColon={false}
                  required
                  shouldRenderTitleAboveField
                  typeField="radio"
                >
                  <div className={styles.containerRadio}>
                    {Object.values(productType).map(({ name, title }: any) => (
                      <Field
                        key={name}
                        name={NamesOfFieldEnum.ProductType}
                        type="radio"
                        value={name}
                        validate={disabledValidate(composeValidators(required()))}
                      >
                        {({ input }) => (
                          <RadioField
                            input={{
                              ...input,
                              onChange: () => {
                                const hasDescription = !!(spyValues[NamesOfFieldEnum.ProductDescription] as string);
                                if (hasDescription && spyValues[NamesOfFieldEnum.ProductType] !== name) {
                                  setProductTypeChangeConfirm({ newValue: name, form: spyForm });
                                } else {
                                  input.onChange(name);
                                }
                              },
                            }}
                          >{title}</RadioField>
                        )}
                      </Field>
                    ))}
                  </div>

                  {productTypeChangeConfirm !== null && (
                    <Modal
                      open
                      title="Изменение типа деятельности"
                      onCancel={() => setProductTypeChangeConfirm(null)}
                      footer={
                        <div style={{ display: 'flex', gap: 16 }}>
                          <CustomButton
                            name="Отмена"
                            type="button"
                            onClick={() => setProductTypeChangeConfirm(null)}
                            className={cn(styles.buttonBackWarningModal, styles.buttonWarningModal)}
                          />
                          <CustomButton
                            name="Продолжить"
                            type="button"
                            onClick={() => {
                              productTypeChangeConfirm.form.change(NamesOfFieldEnum.ProductType, productTypeChangeConfirm.newValue);
                              productTypeChangeConfirm.form.change(NamesOfFieldEnum.ProductDescription, "");
                              setProductTypeChangeConfirm(null);
                            }}
                            className={styles.buttonWarningModal}
                          />
                        </div>
                      }
                    >
                      <p>При смене типа деятельности описание будет сброшено. Продолжить?</p>
                      {spyValues[NamesOfFieldEnum.ProductDescription] && (
                        <div style={{ marginTop: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px' }}>
                          <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Текущее описание:</span>
                          <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.55, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical' }}>{spyValues[NamesOfFieldEnum.ProductDescription] as string}</p>
                        </div>
                      )}
                    </Modal>
                  )}
                </FormField>
              )}
            </FormSpy>
          </FullWidthRowFormContainer>
        )}

        <FullWidthRowFormContainer>
          <FormSpy subscription={{ values: true }}>
            {propsSpy => (
              <>
                {propsSpy.values[NamesOfFieldEnum.ProductType] === "education" && (
                  <FormField
                    titleField={
                      configTitleFieldsOfSelfEmployed[NamesOfFieldEnum.IsHaveEducationalLicense]
                    }
                    name={NamesOfFieldEnum.IsHaveEducationalLicense}
                    isColon={false}
                    required
                    styleContainerTitleField={{ width: "96%" }}
                    shouldRenderTitleAboveField
                  >
                    <div className={styles.containerRadio}>
                      <Field
                        name={NamesOfFieldEnum.IsHaveEducationalLicense}
                        type="radio"
                        value="yes"
                        component={props => <RadioField   {...props} />}
                        validate={disabledValidate(composeValidators(required()))}
                      >
                        Да
                      </Field>
                      <Field
                        name={NamesOfFieldEnum.IsHaveEducationalLicense}
                        type="radio"
                        value="no"
                        component={props => <RadioField   {...props} />}
                        validate={disabledValidate(composeValidators(required()))}
                      >
                        Нет
                      </Field>
                    </div>
                  </FormField>
                )}
              </>
            )}
          </FormSpy>
        </FullWidthRowFormContainer>

        <FullWidthRowFormContainer>
          <RowFieldWithButtonsContainer
            fields={[
              <FormField
                titleField={'Название вашего проекта, которое будут видеть покупатели'}
                name={NamesOfFieldEnum.ProductName}
                required
                validate={composeValidators(required())}
              />
            ]}
            buttons={[]}
          />
        </FullWidthRowFormContainer>

        <FullWidthRowFormContainer>
          <FormSpy subscription={{ values: true }}>
            {({ values, form }) => {
              const productType = values[NamesOfFieldEnum.ProductType];
              const config = getProductDescriptionConfig(productType);
              const hintItemsMap: Record<string, Array<{ title: string; description: string }>> = {
                goods: [
                  { title: "Категория товаров", description: "Укажите категорию: детские игрушки, одежда, аксессуары, типография и т.д.\nОпишите 2–3 предложениями, что именно продаёте." },
                  { title: "Способ доставки", description: "Как доставляете заказы?\nНапример: Почта России, курьерская служба, пункт выдачи, самовывоз." },
                  { title: "Сроки отправки", description: "Через сколько дней после оплаты отправляете заказ?\nНапример: от 1 до 3 рабочих дней после поступления оплаты." },
                ],
                services: [
                  { title: "Направление", description: "Укажите сферу деятельности: красота, психология, маркетинг, нутрициология, юридические услуги и т.д." },
                  { title: "Формат оказания", description: "Как проходит услуга - офлайн или онлайн? Индивидуально или в группе?" },
                  { title: "Способ предоставления", description: "Как клиент получает услугу?\nНапример: личное посещение, видеозвонок, доступ к файлам по ссылке, ведение аккаунта." },
                ],
                education: [
                  { title: "Тема и направление", description: "Укажите сферу: красота, программирование, психология и т.д.\nОпишите суть курса в 2–3 предложениях." },
                  { title: "Формат курсов", description: "Как проходит обучение? Онлайн или офлайн, в группе или индивидуально?" },
                  { title: "Способ предоставления материалов", description: "Где ученик получает доступ к материалам?\nНапример: личный кабинет на платформе, сообщество ВК, доступ к файлам по ссылке, почтовая рассылка." },
                ],
              };
              const exampleMap: Record<string, { lines: Array<{ key: string; value: string }> }> = {
                goods: {
                  lines: [
                    { key: "Категория товаров", value: "детские игрушки - продажа настольных игр, мягких игрушек и конструкторов для детей от 3 лет. Все изделия из сертифицированных материалов." },
                    { key: "Способ доставки", value: "Почта России по всей России." },
                    { key: "Сроки отправки", value: "от 1 до 3 рабочих дней после поступления оплаты." },
                  ]
                },
                services: {
                  lines: [
                    { key: "Направление", value: "ремонт смартфонов, ноутбуков, планшетов (замена экранов, батарей, ПО-восстановление)." },
                    { key: "Формат оказания", value: "личная услуга оффлайн." },
                    { key: "Способ предоставления", value: "личное посещение (выезд или сервисный центр)." },
                  ]
                },
                education: {
                  lines: [
                    { key: "Тема и направление", value: "программирование - курс Python для начинающих и среднего уровня. Изучаем основы языка, алгоритмы и написание реальных проектов." },
                    { key: "Формат курсов", value: "онлайн, группы до 10 человек, занятия 2 раза в неделю." },
                    { key: "Способ предоставления материалов", value: "личный кабинет на платформе Prodamus.XL, доступ к записям сохраняется 1 год после окончания курса." },
                  ]
                },
              };

              return (
                <Field name={NamesOfFieldEnum.ProductDescription} validate={composeValidators(required())}>
                  {({ input, meta }) => (
                    <TextareaModalField
                      name={NamesOfFieldEnum.ProductDescription}
                      label={config.title}
                      value={input.value ?? ""}
                      onChange={input.onChange}
                      placeholder={config.placeholder}
                      maxLength={10000}
                      required
                      error={meta.submitFailed && meta.error ? meta.error : undefined}
                      hintItems={hintItemsMap[productType] ?? []}
                      example={exampleMap[productType]}
                      modalTitle="Описание деятельности"
                      triggerWhiteBg
                      saveBtnGreen
                      isOldUI
                      onBlockedClick={!productType ? () => message.warning(`Чтобы продолжить, нужно выбрать "${configTitleFieldsOfSelfEmployed[NamesOfFieldEnum.ProductType]}"`) : undefined}
                    />
                  )}
                </Field>
              );
            }}
          </FormSpy>
        </FullWidthRowFormContainer>

        <FullWidthRowFormContainer>
          <PayformNameField
            clientId={clientId}
            titleField={configTitleFieldsOfSelfEmployed[NamesOfFieldEnum.PayformName]}
            name={NamesOfFieldEnum.PayformName}
            required
            tooltip="Назовите платёжную ссылку просто и понятно. Это название будут видеть ваши покупатели, например: poshiv.payform.ru"
            typeRequestCheckPayformName={typeRequestCheckPayformName}
            setTypeRequestCheckPayformName={setTypeRequestCheckPayformName}
            errorMsgCheckPayformName={errorMsgCheckPayformName}
            onBlurCallback={callbackCheckPayformName}
          />
        </FullWidthRowFormContainer>

        <FormField
          titleField={configTitleFieldsOfSelfEmployed[NamesOfFieldEnum.PayformPhone]}
          name={NamesOfFieldEnum.PayformPhone}
          required
          tooltip="Это обязательное требование платёжных систем. Номер должен быть действительным"
        >
          <Field
            name={NamesOfFieldEnum.PayformPhone}
            subscription={{ submitFailed: true, error: true, submitError: true, value: true }}
            validate={disabledValidate(
              composeValidators(
                required(),
                testRegExp(/^[+7]/, "Номер телефона должен начинаться на +7")
              )
            )}
            component={props => <PhoneField
              onlyCountries={["ru"]}
              required
              titleField={configTitleFieldsOfSelfEmployed[NamesOfFieldEnum.PayformPhone]}
              {...props}
            />}
          />
        </FormField>

        <FormField
          titleField={configTitleFieldsOfSelfEmployed[NamesOfFieldEnum.PayformEmail]}
          name={NamesOfFieldEnum.PayformEmail}
          required
          validate={composeValidators(required())}
        />

        <FullWidthRowFormContainer>
          <SocialLinkWithModalField
            titleField={configTitleFieldsOfSelfEmployed[NamesOfFieldEnum.PayformLinks]}
            name={NamesOfFieldEnum.PayformLinks}
            required
          />
        </FullWidthRowFormContainer>

        <FullWidthRowFormContainer>
          <PaymentMethodsSelectField
            titleField={configTitleFieldsOfSelfEmployed[NamesOfFieldEnum.PayformPaymentMethods]}
            name={NamesOfFieldEnum.PayformPaymentMethods}
            required
            options={paymentMethod}
          />
        </FullWidthRowFormContainer>

        <FeatureToggle
          flag="is_enabled_integrations_warning"
          NewComponent={
            <FormSpy subscription={{ values: true }}>
              {({ values, form }) => {
                const integrationsValue = values?.[NamesOfFieldEnum.IsNeedExternalIntegrations];
                const recurrentValue = values?.[NamesOfFieldEnum.IsNeedRecurrentPayments];
                const isSelfEmployed = orgType === OrgTypeEnum.SelfEmployed;
                const anyYes = integrationsValue === 'yes' || recurrentValue === 'yes';

                const handleRadioChange = (fieldName: NamesOfFieldEnum, value: string, warningField: WarningField) => {
                  if (!isSelfEmployed || value !== 'yes' || anyYes || warningField !== 'recurrent') {
                    form.change(fieldName, value);
                    return;
                  }
                  setWarningModal({
                    field: warningField,
                    onConfirm: () => {
                      form.change(fieldName, 'yes');
                      setHasAnyYes(true);
                      setWarningModal(null);
                    },
                  });
                };

                return (
                  <>
                    <FullWidthRowFormContainer>
                      <FormField
                        titleField="Нужны ли внешние интеграции (Tilda, Taplink, LeadConverter и пр.)"
                        name={NamesOfFieldEnum.IsNeedExternalIntegrations}
                        shouldRenderTitleAboveField
                        icon={<RubleIconBordered />}
                      >
                        <div className={styles.containerRadio} style={{ marginBottom: 16 }}>
                          <RadioField
                            input={{
                              name: NamesOfFieldEnum.IsNeedExternalIntegrations,
                              value: 'yes',
                              checked: integrationsValue === 'yes',
                              onChange: () => handleRadioChange(
                                NamesOfFieldEnum.IsNeedExternalIntegrations,
                                'yes',
                                'integrations',
                              ),
                            }}
                          >
                            Да
                          </RadioField>
                          <RadioField
                            input={{
                              name: NamesOfFieldEnum.IsNeedExternalIntegrations,
                              value: 'no',
                              checked: integrationsValue === 'no',
                              onChange: () => handleRadioChange(
                                NamesOfFieldEnum.IsNeedExternalIntegrations,
                                'no',
                                'integrations',
                              ),
                            }}
                          >
                            Нет
                          </RadioField>
                        </div>
                      </FormField>
                    </FullWidthRowFormContainer>

                    <FullWidthRowFormContainer>
                      <FormField
                        titleField="Нужны ли вам рекуррентные платежи"
                        tooltip="Это регулярные автоматические списания с карты клиента. Услуга не входит в стандартное подключение и оформляется за доплату"
                        name={NamesOfFieldEnum.IsNeedRecurrentPayments}
                        shouldRenderTitleAboveField
                        icon={<RubleIconBordered />}
                        typeField="radio"
                      >
                        <div className={styles.containerRadio} style={{ marginBottom: 16 }}>
                          <RadioField
                            input={{
                              name: NamesOfFieldEnum.IsNeedRecurrentPayments,
                              value: 'yes',
                              checked: recurrentValue === 'yes',
                              onChange: () => handleRadioChange(
                                NamesOfFieldEnum.IsNeedRecurrentPayments,
                                'yes',
                                'recurrent',
                              ),
                            }}
                          >
                            Да
                          </RadioField>
                          <RadioField
                            input={{
                              name: NamesOfFieldEnum.IsNeedRecurrentPayments,
                              value: 'no',
                              checked: recurrentValue === 'no',
                              onChange: () => handleRadioChange(
                                NamesOfFieldEnum.IsNeedRecurrentPayments,
                                'no',
                                'recurrent',
                              ),
                            }}
                          >
                            Нет
                          </RadioField>
                        </div>
                      </FormField>
                    </FullWidthRowFormContainer>

                    <Modal
                      open={Boolean(warningModal)}
                      onCancel={() => setWarningModal(null)}
                      title="Внимание!"
                      footer={
                        <div style={{ display: 'flex', gap: 8 }}>
                          <CustomButton
                            name="Нет"
                            description="остаться на бесплатном тарифе"
                            type="button"
                            onClick={() => setWarningModal(null)}
                            className={cn(styles.buttonBackWarningModal, styles.buttonWarningModal)}
                          />
                          <CustomButton
                            name="Да"
                            description="подключиться за 10 000 рублей"
                            type="button"
                            onClick={warningModal?.onConfirm}
                            className={cn(styles.buttonWarningModal)}
                          />
                        </div>
                      }
                    >
                      <p style={{ whiteSpace: 'pre-line' }}>
                        {warningModal?.field === 'integrations'
                          ? 'Интеграции доступны только в платной версии'
                          : 'Услуга не входит в стандартное подключение и оформляется за доплату'}
                      </p>

                      <p className={styles.modalFooter}>Планируете подключить данную услугу?</p>
                    </Modal>
                  </>
                );
              }}
            </FormSpy>
          }
          OldComponent={
            <>
              <FullWidthRowFormContainer>
                <FormField
                  titleField="Нужны ли внешние интеграции (Tilda, SaleBot, Bothelp и пр.)"
                  name={NamesOfFieldEnum.IsNeedExternalIntegrations}
                  shouldRenderTitleAboveField
                >
                  <div className={styles.containerRadio} style={{ marginBottom: 16 }}>
                    <Field
                      name={NamesOfFieldEnum.IsNeedExternalIntegrations}
                      type="radio"
                      value="yes"
                      component={RadioField}
                    >
                      Да
                    </Field>
                    <Field
                      name={NamesOfFieldEnum.IsNeedExternalIntegrations}
                      type="radio"
                      value="no"
                      component={RadioField}
                    >
                      Нет
                    </Field>
                  </div>
                </FormField>
              </FullWidthRowFormContainer>

              <FullWidthRowFormContainer>
                <FormField
                  titleField="Нужны ли вам рекуррентные платежи"
                  tooltip="Это регулярные автоматические списания с карты клиента. Услуга не входит в стандартное подключение и оформляется за доплату"
                  name={NamesOfFieldEnum.IsNeedRecurrentPayments}
                  shouldRenderTitleAboveField
                >
                  <div className={styles.containerRadio} style={{ marginBottom: 16 }}>
                    <Field
                      name={NamesOfFieldEnum.IsNeedRecurrentPayments}
                      type="radio"
                      value="yes"
                      component={RadioField}
                    >
                      Да
                    </Field>
                    <Field
                      name={NamesOfFieldEnum.IsNeedRecurrentPayments}
                      type="radio"
                      value="no"
                      component={RadioField}
                    >
                      Нет
                    </Field>
                  </div>
                </FormField>
              </FullWidthRowFormContainer>
            </>
          }
        />

        <FullWidthRowFormContainer>
          <FieldGenerateLogo
            clientId={clientId}
            isLoadingMount={isLoadingMount}
            setLogoFileId={setLogoFileId}
            logoError={logoError}
            setLogoError={setLogoError}
            currentLogoSrc={currentLogoSrc}
            setCurrentLogoSrc={setCurrentLogoSrc}
            logoData={logoData}
            setLogoData={setLogoData}
            setIsLogoProcessing={setIsLogoProcessing}
          />
        </FullWidthRowFormContainer>

        <FullWidthRowFormContainer>
          <CustomButton
            icon={<Icon name="eye" size="xs" className={styles.icon} />}
            name="Посмотреть свою платёжную страницу"
            description="(На основе заполненной информации)"
            onClick={() => setIsPreviewOpen(true)}
            iconPosition="before"
            className={styles.buttonPreviewPage}
          />
        </FullWidthRowFormContainer>

        <PagePreviewModal
          visible={isPreviewOpen}
          logoSrc={currentLogoSrc}
          onClose={() => setIsPreviewOpen(false)}
        />

      </div>
    </V2Wizard.Page>
  );
};
